import IWidget from './iwidget.js';
import { Widget } from './widget.js';
import { WidgetIndex } from './widgetIndex.js';

type ShowIf = {
    field: string;
    op: string;
    value: any;
}

type Observable = {
    observedWidget: IWidget;
    toggleableWidget: IWidget;
    showIf: ShowIf;
}

/**
 * This is a fake widget that is used to resolve the values of special ShowIfs like `settings.foo` or `params.bar`.
 */
export class WidgetLike extends Widget {
    parsedShowIf: ShowIf;

    constructor($node: HTMLElement, args: Record<string, unknown>) {
        super($node, args);

        const [cat, field] = this.parsedShowIf.field.split('.', 2);
        if (!['settings', 'params'].includes(cat)) {
            console.log('WidgetToggler: invalid category', cat);
        }
    }
    async getValue(): Promise<unknown> {
        const [cat, ...field] = this.parsedShowIf.field.split('.');
        if (cat === 'settings') {
            // field can contain a dot, e.g. "settings.user.name"
            // resolve it by splitting the field and accessing the multi-level object
            let value: unknown = window.settings;
            for (const key of field) {
                if (typeof value[key as keyof typeof value] === 'undefined') {
                    console.log('WidgetToggler: could not find setting', key);
                    return '';
                }
                value = value[key as keyof typeof value];
            }
            return value ?? '';
        } if (cat === 'params') {
            const params = new URLSearchParams(window.location.search);
            return params.get(field.join()) ?? window.params[field.join()] ?? ''; // todo test
        }

        return '';
    }
}

/**
 * The WidgetToggler parses the `@ShowIf` annotations and hides/shows fields depending on another field’s value.
 * `WidgetIndex` calls `observe()` for all widgets and `process()` after that.
 */
export class WidgetToggler {
    index: WidgetIndex;
    observables: Observable[] = [];

    constructor(index: WidgetIndex) {
        this.index = index;
    }

    observe(w: IWidget): void {
        const showIfs = WidgetToggler.parseShowIf(w);
        if (showIfs.length === 0) return;

        for (const showIf of showIfs) {
            if (showIf.field.includes('.')) {
                const fakeWidget = new WidgetLike(document.documentElement, { parsedShowIf: showIf });
                this.observables.push({
                    observedWidget: fakeWidget,
                    toggleableWidget: w,
                    showIf,
                });
                continue;
            }
            const field = this.index.findWidget(showIf.field, w.lang, true);

            if (field === false) {
                console.log('WidgetToggler: could not find widget', showIf.field);
                continue;
            }

            this.observables.push({
                observedWidget: field,
                toggleableWidget: w,
                showIf,
            });
        }
    }

    process(): void {
        this.observables.forEach(o => {
            o.observedWidget.listen('change', () => WidgetToggler.handleChange(o, this.index));

            // execute once
            WidgetToggler.handleChange(o, this.index);
        })
    }

    /**
     * This function is called when the observed widget changes.
     * It evaluates the condition and shows/hides the toggleable widget.
     * BEWARE: When adding new conditions, also update `ShowIf` php file.
     */
    static async handleChange(observable: Observable, index: WidgetIndex): Promise<void> {
        const val = await observable.observedWidget.getValue();
        // special types like object (but not null), function, symbol are converted to JSON
        const isSpecial = ['object', 'function', 'symbol'].includes(typeof val) && val !== null;
        const jsonVal = JSON.stringify(val);
        let show = false;

        if (observable.showIf.op === '==') {
            const mightBeNumber = parseInt(observable.showIf.value);
            if (!Number.isNaN(mightBeNumber)) {
                show = (parseInt(val) || 0) === mightBeNumber;
            } else if (isSpecial) {
                show = jsonVal === observable.showIf.value;
            } else {
                // eslint-disable-next-line eqeqeq
                show = val == observable.showIf.value;
            }
        } else if (observable.showIf.op === '!=') {
            if (isSpecial) {
                show = jsonVal !== observable.showIf.value;
            } else {
                // eslint-disable-next-line eqeqeq
                show = val != observable.showIf.value;
            }
        } else if (observable.showIf.op === '>') {
            show = (parseInt(val) ?? 0) > parseInt(observable.showIf.value);
        } else if (observable.showIf.op === '>=') {
            show = (parseInt(val) ?? 0) >= parseInt(observable.showIf.value);
        } else if (observable.showIf.op === '<') {
            show = (parseInt(val) ?? 0) < parseInt(observable.showIf.value);
        } else if (observable.showIf.op === '<=') {
            show = (parseInt(val) ?? 0) <= parseInt(observable.showIf.value);
        } else if (observable.showIf.op === '^=') { // starts with
            show = (typeof val === 'string' ? val : jsonVal).startsWith(observable.showIf.value);
        } else if (observable.showIf.op === '$=') { // ends with
            show = (typeof val === 'string' ? val : jsonVal).endsWith(observable.showIf.value);
        } else if (observable.showIf.op === '*=') { // contains
            show = (typeof val === 'string' ? val : jsonVal).includes(observable.showIf.value);
        } else if (observable.showIf.op === 'matches' && observable.showIf.value instanceof RegExp) { // matches
            show = observable.showIf.value.test(typeof val === 'string' ? val : jsonVal);
        } else {
            console.warn('not a valid toggle condition:', Object.values(observable.showIf).join(' '));
            show = true;
        }

        // set required of toggled widget
        observable.toggleableWidget.required = (show) ? observable.toggleableWidget.originalRequiredValue : false;
        observable.toggleableWidget.$field.classList.toggle('is-hidden', !show);
    }

    static parseShowIf(w: IWidget): ShowIf[] {
        const condition = w.showIf;
        if (typeof condition === 'undefined') return [];

        // split conditions by or, and, || and &&
        const orConditions = condition.split(/ (or|\|\|) /gi).map(c => c.trim()).filter(c => c !== 'or' && c !== '||');
        const andConditions = condition.split(/ (and|&&) /gi).map(c => c.trim()).filter(c => c !== 'and' && c !== '&&');
        if (andConditions.length > 1) {
            console.warn('WidgetToggler: "and" conditions are not supported yet');
        }
        const showIfs: ShowIf[] = [];
        orConditions.forEach((c, i) => {
            let [field, op, value]: any = c.split(' ');
            if (value === 'true') value = true;
            if (value === 'false') value = false;
            if (value === 'null') value = null;
            if (op === 'matches') {
                try {
                    value = new RegExp(value, 'i');
                } catch (e: any) {
                    console.warn(`invalid regex: ${value}`);
                }
            }

            showIfs.push({ field, op, value });
        });

        return showIfs;
    }
}
